import { Fragment, useState } from 'react';
import { Building2, Loader2, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useAdminTeams,
  useAdminCategories,
  useAdminPayoutProviders,
  useCreateTeam,
  useUpdateTeam,
  useDeactivateTeam,
  type CreateTeamInput,
  type InviteChannel,
  type OutreachTeam,
} from '@/hooks/useAdmin';
import { Pill } from '@/components/ui/pill';
import { Tag, channelLabel } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import {
  DataTable,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { INVITE_CHANNELS, type PayoutProviderKey } from '@/api/types';
import { creatorTypeLabel, providerLabel } from '@/lib/labels';

// Single source of truth lives in api/types.ts so this picker can't drift
// from the backend's accepted set or the filter dropdowns elsewhere.

interface FormState {
  code: string;
  displayName: string;
  inviteCategories: string[];
  allowedChannels: InviteChannel[];
  defaultPayoutProviderKey: PayoutProviderKey | '';
}

function emptyForm(): FormState {
  return {
    code: '',
    displayName: '',
    inviteCategories: [],
    allowedChannels: [],
    defaultPayoutProviderKey: '',
  };
}

export function AdminTeams(): JSX.Element {
  const teams = useAdminTeams();
  const categories = useAdminCategories();
  const providers = useAdminPayoutProviders();
  const createMut = useCreateTeam();
  const updateMut = useUpdateTeam();
  const deactivateMut = useDeactivateTeam();

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>(emptyForm);
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);

  if (teams.isLoading || categories.isLoading) {
    return (
      <div className="flex items-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (teams.error || !teams.data) {
    return <p className="text-sm text-destructive">{(teams.error as Error)?.message ?? 'Error'}</p>;
  }

  const activeCategories = (categories.data ?? []).filter((c) => c.isActive);
  const activeProviders = providers.data?.providers ?? [];
  const typeName = (code: string): string =>
    categories.data?.find((c) => c.code === code)?.displayName ?? creatorTypeLabel(code);
  /** Where a team's outreachers are paid when the team has no default of its own. */
  const builtInProvider = (code: string): string | null => {
    const routing = providers.data?.routing;
    if (!routing) return null;
    return (routing.byTeam as Record<string, string>)[code] ?? routing.default;
  };

  function startEdit(team: OutreachTeam): void {
    setEditing(team.code);
    setEditForm({
      code: team.code,
      displayName: team.displayName,
      inviteCategories: team.inviteCategories,
      allowedChannels: team.allowedChannels,
      defaultPayoutProviderKey: team.defaultPayoutProviderKey ?? '',
    });
  }

  function toggleChannel(form: FormState, c: InviteChannel): InviteChannel[] {
    return form.allowedChannels.includes(c)
      ? form.allowedChannels.filter((x) => x !== c)
      : [...form.allowedChannels, c];
  }

  function toggleCategory(form: FormState, code: string): string[] {
    return form.inviteCategories.includes(code)
      ? form.inviteCategories.filter((x) => x !== code)
      : [...form.inviteCategories, code];
  }

  function submitCreate(): void {
    if (!createForm.code || !createForm.displayName) {
      toast.error('Add a code and a name first.');
      return;
    }
    if (createForm.inviteCategories.length === 0) {
      toast.error('Pick at least one creator type.');
      return;
    }
    if (createForm.allowedChannels.length === 0) {
      toast.error('Pick at least one channel.');
      return;
    }
    const body: CreateTeamInput = {
      code: createForm.code,
      displayName: createForm.displayName,
      inviteCategories: createForm.inviteCategories,
      allowedChannels: createForm.allowedChannels,
      defaultPayoutProviderKey: createForm.defaultPayoutProviderKey || null,
    };
    createMut.mutate(body, {
      onSuccess: () => {
        toast.success(`Added ${body.displayName}`);
        setCreateOpen(false);
        setCreateForm(emptyForm());
      },
      onError: (err: Error) => toast.error(`Couldn't add the team: ${err.message}`),
    });
  }

  function submitEdit(code: string): void {
    if (editForm.inviteCategories.length === 0) {
      toast.error('A team needs at least one creator type.');
      return;
    }
    updateMut.mutate(
      {
        code,
        body: {
          displayName: editForm.displayName,
          inviteCategories: editForm.inviteCategories,
          allowedChannels: editForm.allowedChannels,
          defaultPayoutProviderKey: editForm.defaultPayoutProviderKey || null,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Saved ${editForm.displayName}`);
          setEditing(null);
        },
        onError: (err: Error) => toast.error(`Couldn't save the team: ${err.message}`),
      },
    );
  }

  function deactivate(team: OutreachTeam): void {
    if (!team.isActive) return;
    deactivateMut.mutate(team.code, {
      onSuccess: () => toast.success(`${team.displayName} turned off`),
      onError: (err: Error) => toast.error(`Couldn't turn off ${team.displayName}: ${err.message}`),
    });
  }

  function reactivate(team: OutreachTeam): void {
    if (team.isActive) return;
    updateMut.mutate(
      { code: team.code, body: { isActive: true } },
      {
        onSuccess: () => toast.success(`${team.displayName} turned back on`),
        onError: (err: Error) => toast.error(`Couldn't turn on ${team.displayName}: ${err.message}`),
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Teams"
        description="Every outreacher belongs to one team. A team decides which creator types and channels its outreachers can log, and where they're paid by default. A team that's turned off can't take new outreachers, but its existing outreachers keep working."
        actions={
          <Button onClick={() => setCreateOpen((v) => !v)}>
            <Plus className="mr-1 h-4 w-4" /> {createOpen ? 'Cancel' : 'New team'}
          </Button>
        }
      />

      {createOpen && (
        <Card>
          <CardHeader>
            <CardTitle>New team</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <TeamFormFields
              form={createForm}
              onChange={setCreateForm}
              categories={activeCategories.map((c) => ({ code: c.code, displayName: c.displayName }))}
              providers={activeProviders.map((p) => ({ key: p.key, displayName: p.displayName }))}
              toggleChannel={(c) =>
                setCreateForm((f) => ({ ...f, allowedChannels: toggleChannel(f, c) }))
              }
              toggleCategory={(code) =>
                setCreateForm((f) => ({ ...f, inviteCategories: toggleCategory(f, code) }))
              }
              showCode
            />
            <Button onClick={submitCreate} disabled={createMut.isPending}>
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </CardContent>
        </Card>
      )}

      <DataTable>
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell>Team</DataTableHeaderCell>
            <DataTableHeaderCell>Creator types</DataTableHeaderCell>
            <DataTableHeaderCell>Channels</DataTableHeaderCell>
            <DataTableHeaderCell>Paid through</DataTableHeaderCell>
            <DataTableHeaderCell>Status</DataTableHeaderCell>
            <DataTableHeaderCell className="text-right">Actions</DataTableHeaderCell>
          </tr>
        </DataTableHeader>
        <tbody>
          {teams.data.map((team) => {
            const isEditing = editing === team.code;
            return (
              <Fragment key={team.code}>
                <DataTableRow className="align-top">
                  <td className="py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <Building2
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      {team.displayName}
                    </span>
                  </td>
                  <td className="py-2 text-xs" data-label="Creator types">
                    {team.inviteCategories.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {team.inviteCategories.map((c) => (
                          <Tag key={c}>{typeName(c)}</Tag>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2 text-xs" data-label="Channels">
                    {team.allowedChannels.map((c) => channelLabel(c)).join(', ') || '—'}
                  </td>
                  <td className="py-2 text-xs" data-label="Paid through">
                    {team.defaultPayoutProviderKey ? (
                      providerLabel(team.defaultPayoutProviderKey)
                    ) : (
                      <span className="text-muted-foreground">
                        {builtInProvider(team.code)
                          ? `${providerLabel(builtInProvider(team.code))} (built-in default)`
                          : 'Built-in default'}
                      </span>
                    )}
                  </td>
                  <td className="py-2" data-label="Status">
                    <Pill tone={team.isActive ? 'paid' : 'muted'}>
                      {team.isActive ? 'Active' : 'Inactive'}
                    </Pill>
                  </td>
                  <td className="actions py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(team)}
                      aria-label={`Edit ${team.displayName}`}
                      title="Edit team"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {team.isActive ? (
                      <ConfirmDialog
                        title={`Turn off ${team.displayName}?`}
                        description={
                          <>
                            Outreachers already on {team.displayName} keep working: their open
                            invites, payouts in progress and portal access stay live. New
                            outreachers can&apos;t be added to it until it&apos;s turned back on.
                          </>
                        }
                        tone="destructive"
                        confirmLabel="Turn off team"
                        pending={deactivateMut.isPending}
                        onConfirm={() => deactivate(team)}
                        trigger={
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Turn off"
                            aria-label={`Turn off ${team.displayName}`}
                          >
                            <Power className="text-destructive-strong h-4 w-4" />
                          </Button>
                        }
                      />
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => reactivate(team)}
                        disabled={updateMut.isPending}
                        title="Turn back on"
                        aria-label={`Turn ${team.displayName} back on`}
                      >
                        <Power className="text-success-strong h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </DataTableRow>
                {isEditing && (
                  <DataTableRow state="expanded">
                    <td colSpan={6} className="p-4">
                      <div className="space-y-3">
                        <TeamFormFields
                          form={editForm}
                          onChange={setEditForm}
                          categories={activeCategories.map((c) => ({ code: c.code, displayName: c.displayName }))}
                          providers={activeProviders.map((p) => ({ key: p.key, displayName: p.displayName }))}
                          toggleChannel={(c) =>
                            setEditForm((f) => ({ ...f, allowedChannels: toggleChannel(f, c) }))
                          }
                          toggleCategory={(code) =>
                            setEditForm((f) => ({ ...f, inviteCategories: toggleCategory(f, code) }))
                          }
                        />
                        <div className="flex gap-2">
                          <Button onClick={() => submitEdit(team.code)} disabled={updateMut.isPending}>
                            {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save
                          </Button>
                          <Button variant="ghost" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </td>
                  </DataTableRow>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </DataTable>
    </div>
  );
}

interface FormFieldsProps {
  form: FormState;
  onChange: (next: FormState) => void;
  categories: Array<{ code: string; displayName: string }>;
  providers: Array<{ key: PayoutProviderKey; displayName: string }>;
  toggleChannel: (c: InviteChannel) => void;
  toggleCategory: (code: string) => void;
  showCode?: boolean;
}

function TeamFormFields({
  form,
  onChange,
  categories,
  providers,
  toggleChannel,
  toggleCategory,
  showCode,
}: FormFieldsProps): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {showCode && (
        <div className="grid gap-1.5">
          <Label htmlFor="team-code">Code</Label>
          <Input
            id="team-code"
            value={form.code}
            onChange={(e) => onChange({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="PARTNERSHIPS"
          />
          <p className="text-xs text-muted-foreground">
            A short ID: A–Z, 0–9 and underscores. It can&apos;t be changed later.
          </p>
        </div>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="team-display">Name</Label>
        <Input
          id="team-display"
          value={form.displayName}
          onChange={(e) => onChange({ ...form, displayName: e.target.value })}
          placeholder="Partnerships"
        />
      </div>
      <div className="grid gap-1.5 sm:col-span-2">
        <Label>Creator types</Label>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => {
            const active = form.inviteCategories.includes(c.code);
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => toggleCategory(c.code)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-transparent text-foreground hover:bg-muted'
                }`}
                aria-pressed={active}
              >
                {c.displayName}
              </button>
            );
          })}
          {categories.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No active creator types yet. Add one under Compensation → Creator types first.
            </p>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Outreachers on this team pick from these when logging an invite.
        </p>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="team-provider">Paid through (team default)</Label>
        <Combobox
          value={form.defaultPayoutProviderKey}
          onChange={(v) =>
            onChange({ ...form, defaultPayoutProviderKey: v as PayoutProviderKey | '' })
          }
          options={providers.map((p) => ({
            value: p.key,
            label: providerLabel(p.key),
          }))}
          allowAll
          allLabel="Built-in default"
        />
        <p className="text-xs text-muted-foreground">
          Applies to every outreacher on the team unless that outreacher has their own override.
        </p>
      </div>
      <div className="grid gap-1.5 sm:col-span-2">
        <Label>Channels</Label>
        <div className="flex flex-wrap gap-2">
          {INVITE_CHANNELS.map((c) => {
            const active = form.allowedChannels.includes(c);
            return (
              <button
                key={c}
                type="button"
                aria-pressed={active}
                onClick={() => toggleChannel(c)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input bg-transparent text-foreground hover:bg-muted'
                }`}
              >
                {channelLabel(c)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
